import React, { type FC, memo, type JSX } from 'react';
import { sumClasses } from '@/app/components/search/utils/misc';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation';
import { DayStyled } from './styled';

export type DayProps = {
  date?: Date;
  day?: string | number;
  dayOfWeek?: number;
  dataDate?: string;
  className?: string;
};

const Day: FC<DayProps> = memo(
  ({ date, day, dayOfWeek, dataDate, className }): JSX.Element => {
    const { localization } = usePageWrapperData();
    const { t } = useTranslation();
    const isEmpty = !date;
    const isDisabled = className?.includes('day-disabled');
    const isSelected =
      className?.includes('day-active') ||
      className?.includes('day-active-start') ||
      className?.includes('day-active-end') ||
      className?.includes('day-active-exact');

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
      if (!isEmpty && !isDisabled && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        (e.currentTarget as HTMLDivElement).click();
      }
    };

    const getAriaLabel = (): string => {
      if (!date) return '';
      const dateStr = date.toLocaleDateString(localization.locale, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      if (isSelected) {
        return `${dateStr}, ${t('vacation.oss.components.autocomplete.other.selected')}`;
      }
      if (isDisabled) {
        return `${dateStr}, ${t('vacation.select.details.pricecalendar.date.notavailable')}`;
      }
      return dateStr;
    };

    return (
      <DayStyled
        {...{ date }}
        className={sumClasses([!date ? 'day-empty' : '', 'day', className || ''])}
        data-day={day}
        data-date={dataDate}
        data-day-of-week={dayOfWeek}
        tabIndex={isEmpty || isDisabled ? -1 : 0}
        role={isEmpty ? undefined : 'button'}
        onKeyDown={handleKeyDown}
        aria-label={getAriaLabel()}
        aria-pressed={!isEmpty && !isDisabled ? isSelected : undefined}
        aria-disabled={!isEmpty && isDisabled ? true : undefined}
      >
        {date && date.getDate()}
      </DayStyled>
    );
  },
  (prevProps: DayProps, nextProps: DayProps) => {
    if (prevProps.className !== nextProps.className) return false;
    return true;
  }
);

Day.displayName = 'Day';

export default Day;
